import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, getErrorTranslationKey } from '@/components/auth/AuthProvider';
import { AuthError } from '@/types/authErrors';

/**
 * OAuth Callback Page
 * Feature: 008-better-auth-integration
 * 
 * Handles OAuth redirect callback from Google.
 * Processes the OAuth response and redirects to dashboard or shows error.
 */
export default function AuthCallback() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const { handleOAuthCallback, isAuthenticated, isAdmin, error } = useAuth();
  const [processing, setProcessing] = useState(true);
  const [callbackError, setCallbackError] = useState<AuthError | null>(null);

  useEffect(() => {
    async function processCallback() {
      try {
        await handleOAuthCallback();
        // Success - will redirect below based on isAuthenticated
      } catch (err) {
        if (err instanceof AuthError) {
          setCallbackError(err);
        } else {
          setCallbackError(AuthError.fromApiError({
            message: 'OAuth callback failed',
            code: 'OAUTH_ERROR',
            status: 400
          }));
        }
      } finally {
        setProcessing(false);
      }
    }

    processCallback();
  }, [handleOAuthCallback]);

  // Redirect on success
  useEffect(() => {
    if (!processing && isAuthenticated && isAdmin) {
      navigate('/admin/dashboard', { replace: true });
    }
  }, [processing, isAuthenticated, isAdmin, navigate]);

  // Show error state
  const displayError = callbackError || error;
  if (displayError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full space-y-6 text-center">
          <div className="rounded-md bg-red-50 p-6 border border-red-200">
            <svg className="mx-auto h-12 w-12 text-red-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h2 className="text-lg font-semibold text-red-800 mb-2">
              {t('errors.oauthError')}
            </h2>
            <p className="text-sm text-red-600 mb-4">
              {t(`errors.${getErrorTranslationKey(displayError.type)}`)}
            </p>
            <button
              onClick={() => navigate('/admin/login', { replace: true })}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              {t('unauthorized.goHome')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-gray-600">{t('login.loggingIn')}</p>
      </div>
    </div>
  );
}
